import { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import api from "../../api/api";
import { Upload, X } from "lucide-react";

export default function AdminTourForm({ tour, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category_id: "",
    location: "",
    duration: "",
    price: "",
    discount_price: "",
    image_url: "",
    gallery_images: [],
    max_participants: "",
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (tour) setFormData(tour);
  }, [tour]);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) setFormData((prev) => ({ ...prev, image_url: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key !== "image_url") {
          if (key === "gallery_images") {
            data.append(key, JSON.stringify(formData[key]));
          } else {
            data.append(key, formData[key]);
          }
        }
      });

      if (formData.image_url instanceof File) {
        data.append("image", formData.image_url);
      } else if (tour?.image_url) {
        data.append("image_url", tour.image_url);
      }

      if (tour?.id) {
        await api.put(`/tours/${tour.id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/tours", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      onSave();
    } catch (error) {
      console.error("Error saving tour:", error);
      alert("Error saving tour");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto bg-white shadow-lg rounded-xl p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between border-b pb-3 mb-5">
        <h2 className="text-2xl font-semibold text-gray-800">
          {tour?.id ? "Edit Tour" : "Create New Tour"}
        </h2>
        <button
          className="text-gray-500 hover:text-red-500 transition"
          onClick={onCancel}
        >
          <X size={26} />
        </button>
      </div>

      {/* FORM */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* GRID INPUTS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Title */}
          <div className="flex flex-col">
            <label className="font-medium text-gray-700">Tour Title *</label>
            <input
              type="text"
              name="title"
              className="border border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200"
              value={formData.title}
              required
              onChange={handleChange}
              placeholder="Enter tour title"
            />
          </div>

          {/* Category */}
          <div className="flex flex-col">
            <label className="font-medium text-gray-700">Category *</label>
            <select
              name="category_id"
              value={formData.category_id}
              required
              onChange={handleChange}
              className="border border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div className="flex flex-col">
            <label className="font-medium text-gray-700">Location *</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              required
              onChange={handleChange}
              placeholder="Enter location"
              className="border border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200"
            />
          </div>

          {/* Duration */}
          <div className="flex flex-col">
            <label className="font-medium text-gray-700">Duration (days) *</label>
            <input
              type="number"
              name="duration"
              value={formData.duration}
              required
              onChange={handleChange}
              className="border border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200"
              placeholder="e.g., 5"
            />
          </div>

          {/* Price */}
          <div className="flex flex-col">
            <label className="font-medium text-gray-700">Price *</label>
            <input
              type="number"
              step="0.01"
              name="price"
              value={formData.price}
              required
              onChange={handleChange}
              className="border border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200"
              placeholder="0.00"
            />
          </div>

          {/* Discount */}
          <div className="flex flex-col">
            <label className="font-medium text-gray-700">Discount Price</label>
            <input
              type="number"
              step="0.01"
              name="discount_price"
              value={formData.discount_price}
              onChange={handleChange}
              className="border border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200"
              placeholder="0.00"
            />
          </div>

          {/* Max Participants */}
          <div className="flex flex-col">
            <label className="font-medium text-gray-700">Max Participants</label>
            <input
              type="number"
              name="max_participants"
              value={formData.max_participants}
              onChange={handleChange}
              className="border border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200"
              placeholder="e.g., 20"
            />
          </div>
        </div>

        {/* IMAGE UPLOAD */}
        <div>
          <label className="font-medium text-gray-700 block pb-1">
            Main Image *
          </label>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition">
            <input
              type="file"
              id="image"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />

            <label
              htmlFor="image"
              className="flex flex-col items-center cursor-pointer gap-2"
            >
              <Upload size={28} className="text-gray-600" />
              <span className="text-gray-600">Click to upload or drag & drop</span>
            </label>

            {formData.image_url && (
              <div className="mt-4 flex justify-center">
                <img
                  src={
                    formData.image_url instanceof File
                      ? URL.createObjectURL(formData.image_url)
                      : `/uploads/tours/${formData.image_url}`
                  }
                  alt="Preview"
                  className="w-40 h-40 object-cover rounded-lg shadow-md"
                />
              </div>
            )}
          </div>
        </div>

        {/* DESCRIPTION */}
        <div>
          <label className="font-medium text-gray-700 block pb-1">
            Description *
          </label>
          <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={handleQuillChange}
            placeholder="Enter detailed tour description"
          />
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2 border rounded-lg text-gray-700 hover:bg-gray-200 transition"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400"
          >
            {loading ? "Saving..." : tour?.id ? "Update Tour" : "Create Tour"}
          </button>
        </div>
      </form>
    </div>
  );
}
